import { gql } from '@apollo/client'
import React, { useState } from 'react'
import * as yup from "yup"
import { Row, Col, Alert, Button, UncontrolledTooltip } from 'reactstrap'
import { useApolloClient, useQuery } from '@apollo/client'
import toTitleCase from '../../Functions/toTitleCase'
import CustomYupInputForm from '../../Material/CustomYupInputForm'
import { PeopleSchema } from '../IncidentModals/IncidentModalsSchema'
import SearchPlaces from '../../Material/SearchPlaces'
import AddRepresentativeModal from '../IncidentModals/AddRepresentativeModal'
import { SEAT_ARRANGEMENT } from '../../Constants'

const UpdateIncidentPartyPeople = gql`
    mutation updateIncidentPartyPeopleMutation($input: UpdateIncidentPartyPeopleMutationInput!){
    updateIncidentPartyPeople(input:$input){
        ok
        errors{
            field
            messages
        }
    }
} `


const IncidentPartyPeopleInjuriesChoices = gql`
    query IncidentPartyPeopleInjuriesChoices {
        incidentPartyPeopleInjuriesChoices: __type(name: "IncidentsIncidentPartyPeopleInjuriesChoices") {
        enumValues {
            name
            description
        }
    }
}`

const AllIncidentPartyRepresentatives = gql`
    query AllIncidentPartyRepresentatives {
        allIncidentPartyRepresentatives {
            edges {
                node {
                    id
                    name
                    businessName
                }
            }
        }
    }
`


const PersonInfo = ({ person, refetchQuery, isDefault, hasRental, colSize = 3, passengerPosition }) => {
    const apolloClient = useApolloClient() // Get the Apollo Client instance
    const [editField, setEditField] = useState("")
    const [inputValues, setInputValues] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [error, setError] = useState(null)
    const [openAddRepresentativeModal, setOpenAddRepresentativeModal] = useState(false) // State for AddRepresentativeModal
    const { data: injuriesChoices } = useQuery(IncidentPartyPeopleInjuriesChoices)
    const { data: representativesData, refetch: refetchRepresentatives } = useQuery(AllIncidentPartyRepresentatives)


    const editFieldHandler = (fieldName, fieldValue = "") => {
        setEditField(fieldName)
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
    }

    const saveFieldHandler = (partyPeopleId, fieldName, fieldValue) => {
        if (fieldErrors[fieldName]) return
        if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
            cancelEditHandler()
            return
        }
        updatePartyPeopleHandler(partyPeopleId, fieldName, fieldValue)
    }


    const updatePartyPeopleHandler = async (partyPeopleId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }

            const isValidValue = typeof trimmedValue === "boolean" || Boolean(trimmedValue)
            if (!partyPeopleId || !name || !isValidValue) return

            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError("")

            const input = {
                partyPeopleId: partyPeopleId,
                [name]: trimmedValue
            }

            try {
                const result = await apolloClient.mutate({
                    mutation: UpdateIncidentPartyPeople,
                    variables: { input },
                })

                const { ok, errors } = result?.data?.updateIncidentPartyPeople || {}

                if (ok) {
                    setEditField("")
                    setError("")
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetchQuery()
                } else {
                    const errorMessage = errors?.[0]?.messages?.toString() ||
                        "Something went wrong, Please contact admin for support!"
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField("")
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: "" }))
        }
    }
    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === "select" ? rawValue === "true" ? true : rawValue === "false" ? false : rawValue : rawValue
        }))
    }

    const cancelEditHandler = () => {
        if (!editField) return
        setEditField("")
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setError("")
        setFieldLoading({})
    }

    const handleAddRepresentative = async (newRepresentative) => {
        await refetchRepresentatives()
        setInputValues(prev => ({ ...prev, representativeId: newRepresentative.id }))
        setOpenAddRepresentativeModal(false)
    }

    const allowEdit = !(isDefault && person.role === "VEHICLE_DRIVER") || !hasRental

    return (
        <Row key={person?.id}>
            {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
            {<Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Role"
                    fieldName={"role"}
                    inputType="text"
                    isEditing={editField === "role"}
                    isLoading={!!fieldLoading?.role}
                    inputValue={inputValues?.["role"]}
                    defaultValue={toTitleCase(person?.role)}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "role")}
                />
            </Col>}
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Name"
                    fieldName={"name"}
                    inputType="text"
                    showEdit={allowEdit}
                    isEditing={editField === "name"}
                    isLoading={!!fieldLoading?.name}
                    inputValue={inputValues?.["name"]}
                    defaultValue={person?.name}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "name")}
                />
            </Col>
            {person?.role === "VEHICLE_PASSENGER" && (
                <Col xs={colSize} className="mb-2">
                    <CustomYupInputForm
                        label="Seat Position"
                        fieldName={"passengerPosition"}
                        inputType="select"
                        options={SEAT_ARRANGEMENT}
                        showEdit={allowEdit}
                        isEditing={editField === "passengerPosition"}
                        isLoading={!!fieldLoading?.passengerPosition}
                        inputValue={inputValues?.["passengerPosition"]}
                        defaultValue={passengerPosition || ""}
                        onEdit={editFieldHandler}
                        onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                        onChange={updateInputHandler}
                        onCancel={cancelEditHandler}
                        yupSchema={yup.string().nullable()}
                    />
                </Col>
            )}
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Email"
                    fieldName={"email"}
                    inputType="email"
                    showEdit={allowEdit}
                    isEditing={editField === "email"}
                    isLoading={!!fieldLoading?.email}
                    inputValue={inputValues?.["email"]}
                    defaultValue={person?.email}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "email")}
                />
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Phone"
                    fieldName={"phoneNumber"}
                    inputType="text"
                    showEdit={allowEdit}
                    isEditing={editField === "phoneNumber"}
                    isLoading={!!fieldLoading?.phoneNumber}
                    inputValue={inputValues?.["phoneNumber"]}
                    defaultValue={person?.phoneNumber}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "phoneNumber")}
                />
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="License"
                    fieldName={"driverLicense"}
                    inputType="text"
                    showEdit={allowEdit}
                    isEditing={editField === "driverLicense"}
                    isLoading={!!fieldLoading?.driverLicense}
                    inputValue={inputValues?.["driverLicense"]}
                    defaultValue={person?.driverLicense}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "driverLicense")}
                />
            </Col>
            <Col xs={colSize}>
                <CustomYupInputForm
                    label="Address"
                    fieldName={"address"}
                    inputType="text"
                    showEdit={allowEdit}
                    isEditing={editField === "address"}
                    isLoading={!!fieldLoading?.address}
                    inputValue={inputValues?.["address"]}
                    defaultValue={person?.address}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "address")}
                    renderCustomInput={() =>
                        <SearchPlaces
                            onSaveAction={(place) => { setInputValues({ ...inputValues, address: place?.name || "" }) }}
                            initialValue={person?.address || ""}
                            searchType="address"
                        />
                    }
                />
            </Col>
            <Col xs={colSize} className="mb-2 d-flex align-items-center">
                <CustomYupInputForm
                    label="Representative"
                    fieldName={"representativeId"}
                    inputType="select"
                    showEdit
                    isEditing={editField === "representativeId"}
                    isLoading={!!fieldLoading?.respresentative}
                    inputValue={inputValues?.["representativeId"]}
                    options={representativesData?.allIncidentPartyRepresentatives?.edges?.map(({ node }) => ({ label: node.businessName + " - " + node.name, value: node.id }))}
                    defaultValue={person?.respresentative?.id || ""}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                />
                {editField === "representativeId" &&
                    <Button color="primary" onClick={() => setOpenAddRepresentativeModal(true)} className='mt-4 p-2' id="AddRepresentative">
                        <i className='fa fa-plus' />
                        <UncontrolledTooltip target={"AddRepresentative"}>Add Representative</UncontrolledTooltip>
                    </Button>
                }
                {openAddRepresentativeModal && (
                    <AddRepresentativeModal
                        isOpen={openAddRepresentativeModal}
                        toggle={() => setOpenAddRepresentativeModal(false)}
                        onCreate={handleAddRepresentative}
                        refetchRepresentatives={refetchRepresentatives}
                    />
                )}
            </Col>
            <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Injuries"
                    fieldName={"injuries"}
                    inputType="select"
                    showEdit
                    options={injuriesChoices?.incidentPartyPeopleInjuriesChoices?.enumValues?.map(choice => ({ label: choice.name, value: choice.name }))}
                    isEditing={editField === "injuries"}
                    isLoading={!!fieldLoading?.injuries}
                    inputValue={inputValues?.["injuries"]}
                    defaultValue={person?.injuries}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "injuries")}
                />
            </Col>
            {!(isDefault && person.role === "VEHICLE_DRIVER") && <Col xs={colSize} className="mb-2">
                <CustomYupInputForm
                    label="Is Business Entity"
                    fieldName={"isBusinessEntity"}
                    inputType="select"
                    showEdit={allowEdit}
                    options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
                    isEditing={editField === "isBusinessEntity"}
                    isLoading={!!fieldLoading?.isBusinessEntity}
                    inputValue={inputValues?.["isBusinessEntity"]}
                    defaultValue={person?.isBusinessEntity}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "isBusinessEntity")}
                />
            </Col>}
            <Col xs={12} className="mb-2">
                <CustomYupInputForm
                    label="Statement"
                    fieldName={"statement"}
                    inputType="text"
                    showEdit={allowEdit}
                    isEditing={editField === "statement"}
                    isLoading={!!fieldLoading?.statement}
                    inputValue={inputValues?.["statement"]}
                    defaultValue={person?.statement}
                    onEdit={editFieldHandler}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(person.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={yup.reach(PeopleSchema, "statement")}
                />
            </Col>
        </Row>
    )
}

export default PersonInfo
